import { createContext, useState, type ReactNode } from 'react';
import { Outlet, useLocation } from '@tanstack/react-router';
import { AppShell } from './components/AppShell.tsx';
import { CookieBanner } from './components/CookieBanner.tsx';
import { LandingPage } from './components/LandingPage.tsx';
import { LoginForm } from './components/LoginForm.tsx';
import { RegisterForm } from './components/RegisterForm.tsx';
import { SkipLink, MAIN_CONTENT_ID } from './components/SkipLink.tsx';
import { useAuth } from './auth/useAuth.ts';

type GateMode = 'landing' | 'login' | 'register';

/** What `/try` hands its routed content (`TryRoute`, several layers below
 * `<Outlet />`) so the post-verdict "create an account" CTA can flip the
 * same `mode` state this file owns, the same way `PublicFixturesShell`'s own
 * CTA does — a plain prop can't cross an `<Outlet />` boundary, so this is
 * the one piece of gate state anything under `/try` needs from here. `null`
 * outside `PublicTryShell` (there's nothing to switch to). */
export const PublicTryActionsContext = createContext<{ onGetStarted: () => void } | null>(null);

/** The router's root route component (see `router.tsx`) — decides between
 * the signed-out landing/login/register flow and the signed-in shell, and
 * renders whichever of the three routes (squad desk, fixtures, tracker) is
 * active via `<Outlet />`. `SquadProvider` is mounted above the router in
 * `main.tsx`, not here, so every route can read the loaded squad.
 *
 * `/fixtures`, `/try` and `/record-public` are the exceptions (T-049, T-088,
 * T-094): a signed-out visitor there sees the grid, the public squad itself,
 * or the aggregate honesty ledger, not the landing page, as long as they
 * haven't already started the sign-up/sign-in flow (`mode` still
 * `'landing'`). Lifted up here rather than left inside `LoginGate` so that
 * flow can be reached from `PublicFixturesShell`'s and `PublicTryShell`'s
 * own CTAs too, and both `mode` and `status` decide which of the four things
 * a visit to any of the three routes can look like: the surface plus a
 * sign-up prompt, the surface replaced by a login/register form, or — signed
 * in — the real, authenticated route. */
export function App() {
  const { status } = useAuth();
  const location = useLocation();
  const [mode, setMode] = useState<GateMode>('landing');

  const isSignedOutLanding = status === 'unauthenticated' && mode === 'landing';
  const isPublicFixtures = isSignedOutLanding && location.pathname.startsWith('/fixtures');
  const isPublicTry = isSignedOutLanding && location.pathname.startsWith('/try');
  const isPublicRecord = isSignedOutLanding && location.pathname.startsWith('/record-public');

  return (
    <>
      {status === 'loading' ? null : isPublicFixtures ? (
        <PublicFixturesShell onGetStarted={() => setMode('register')} onSignIn={() => setMode('login')}>
          <Outlet />
        </PublicFixturesShell>
      ) : isPublicTry ? (
        <PublicTryShell onGetStarted={() => setMode('register')} onSignIn={() => setMode('login')}>
          <Outlet />
        </PublicTryShell>
      ) : isPublicRecord ? (
        <PublicRecordShell onSignIn={() => setMode('login')}>
          <Outlet />
        </PublicRecordShell>
      ) : status === 'unauthenticated' ? (
        <LoginGate mode={mode} onModeChange={setMode} />
      ) : (
        <AppShell>
          <Outlet />
        </AppShell>
      )}
      {/* Outside the branches above so it shows on first visit regardless of
       * auth state — consent applies the moment the page loads, not once
       * someone's logged in. */}
      <CookieBanner />
    </>
  );
}

interface LoginGateProps {
  mode: GateMode;
  onModeChange: (mode: GateMode) => void;
}

/** The signed-out experience: a marketing landing page explaining what the
 * product does, first — not the login form dropped cold. "Get started"
 * heads to register, "Sign in" to login; either form can switch to the
 * other, but only the landing page's own CTAs (or `PublicFixturesShell`'s)
 * enter the flow at all. */
function LoginGate({ mode, onModeChange }: LoginGateProps) {
  if (mode === 'landing') {
    return <LandingPage onGetStarted={() => onModeChange('register')} onSignIn={() => onModeChange('login')} />;
  }

  return (
    <div className="shell">
      <SkipLink />

      <header className="masthead">
        <h1 className="wordmark">
          The <em>Gaffer</em>
        </h1>
        <span className="strap">Fantasy Premier League · squad desk</span>
      </header>

      <main id={MAIN_CONTENT_ID} tabIndex={-1}>
        {mode === 'login' ? (
          <LoginForm onSwitchToRegister={() => onModeChange('register')} />
        ) : (
          <RegisterForm onSwitchToLogin={() => onModeChange('login')} />
        )}
      </main>
    </div>
  );
}

interface PublicFixturesShellProps {
  children: ReactNode;
  onGetStarted: () => void;
  onSignIn: () => void;
}

/** The signed-out `/fixtures` shell (T-049) — the same masthead and footer
 * as `AppShell`, but with the squad-specific main nav and `SquadContextBar`
 * replaced by a one-line pitch for what an account adds (a squad analysed
 * against these fixtures, not the grid on its own) plus its own CTA. Keeps
 * `/fixtures` a genuine entry point rather than a stray page with no way
 * onward, without pretending a signed-out visitor has a squad or other
 * routes to switch to. */
function PublicFixturesShell({ children, onGetStarted, onSignIn }: PublicFixturesShellProps) {
  return (
    <div className="shell">
      <SkipLink />

      <header className="masthead">
        <h1 className="wordmark">
          The <em>Gaffer</em>
        </h1>
        <button type="button" className="ghost" onClick={onSignIn}>
          Sign in
        </button>
      </header>

      <section className="context-bar context-bar--cta" aria-label="Sign up">
        <p className="hint">Sign up free to see your own squad analysed against these fixtures.</p>
        <button type="button" className="ghost small" onClick={onGetStarted}>
          Get started free
        </button>
      </section>

      <main id={MAIN_CONTENT_ID} tabIndex={-1}>{children}</main>

      <footer>
        Not affiliated with the Premier League or Fantasy Premier League. Advice is generated and
        can be wrong — you are still the manager.
      </footer>
    </div>
  );
}

interface PublicTryShellProps {
  children: ReactNode;
  onGetStarted: () => void;
  onSignIn: () => void;
}

/** The signed-out `/try` shell (T-088) — the same masthead and footer as
 * `PublicFixturesShell`, but with no persistent CTA bar of its own: unlike
 * the fixture grid, `/try` already argues for an account at the one moment
 * that argument means something (see `PublicVerdict`'s post-verdict panel),
 * so a second, generic banner above it would just be noise. `onGetStarted`
 * reaches that panel via `PublicTryActionsContext` instead of a prop, since
 * `TryRoute` is rendered by `<Outlet />`, several layers below this
 * component. Exported (T-102) so `a11y.test.tsx` can render it directly,
 * the same way it renders `AppShell`, rather than routing through `<App />`
 * and a mocked `<Outlet />` just to reach the skip link. */
export function PublicTryShell({ children, onGetStarted, onSignIn }: PublicTryShellProps) {
  return (
    <div className="shell">
      <SkipLink />

      <header className="masthead">
        <h1 className="wordmark">
          The <em>Gaffer</em>
        </h1>
        <button type="button" className="ghost" onClick={onSignIn}>
          Sign in
        </button>
      </header>

      <main id={MAIN_CONTENT_ID} tabIndex={-1}>
        <PublicTryActionsContext.Provider value={{ onGetStarted }}>{children}</PublicTryActionsContext.Provider>
      </main>

      <footer>
        Not affiliated with the Premier League or Fantasy Premier League. Advice is generated and
        can be wrong — you are still the manager.
      </footer>
    </div>
  );
}

interface PublicRecordShellProps {
  children: ReactNode;
  onSignIn: () => void;
}

/** The signed-out `/record-public` shell (T-094) — the same masthead and
 * footer as the other two public shells, but no sign-up CTA of its own: the
 * page's whole argument is that it reads like a record, not a pitch (see
 * `PublicRecord`'s own comment), so a banner asking for an account on top of
 * it would undercut exactly the thing making it persuasive. Exported
 * (T-102) for the same reason `PublicTryShell` is — direct rendering in
 * `a11y.test.tsx`. */
export function PublicRecordShell({ children, onSignIn }: PublicRecordShellProps) {
  return (
    <div className="shell">
      <SkipLink />

      <header className="masthead">
        <h1 className="wordmark">
          The <em>Gaffer</em>
        </h1>
        <button type="button" className="ghost" onClick={onSignIn}>
          Sign in
        </button>
      </header>

      <main id={MAIN_CONTENT_ID} tabIndex={-1}>{children}</main>

      <footer>
        Not affiliated with the Premier League or Fantasy Premier League. Advice is generated and
        can be wrong — you are still the manager.
      </footer>
    </div>
  );
}
